/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect, useMemo } from 'react';
import {
    ClipboardDocumentIcon,
    CheckIcon,
    InformationCircleIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    EyeSlashIcon,
    KeyIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Bucket, BucketCredentials } from '../../types';
import { UpdateBucketData } from '../../hooks/useBuckets';
import { bucketService } from '../../services/api';

interface BucketSettingsPanelProps {
    bucket: Bucket;
    projectId: string;
    onUpdate: (data: UpdateBucketData) => Promise<void>;
    onDelete: () => void;
    isUpdating: boolean;
    updateError?: string | null;
}

const BucketSettingsPanel: React.FC<BucketSettingsPanelProps> = ({
    bucket,
    projectId,
    onUpdate,
    onDelete,
    isUpdating,
    updateError,
}) => {
    // Form state for editable fields
    const [formData, setFormData] = useState({
        visibility: bucket.is_public ? 'public' : 'private',
        description: bucket.description || '',
        versioning: bucket.versioning,
    });

    // Track if ID was copied
    const [copiedId, setCopiedId] = useState(false);

    // Credentials state
    const [credentials, setCredentials] = useState<BucketCredentials | null>(null);
    const [credentialsLoading, setCredentialsLoading] = useState(true);
    const [credentialsError, setCredentialsError] = useState<string | null>(null);
    const [showSecret, setShowSecret] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [showMountSteps, setShowMountSteps] = useState(false);

    // Reset form when bucket changes
    useEffect(() => {
        setFormData({
            visibility: bucket.is_public ? 'public' : 'private',
            description: bucket.description || '',
            versioning: bucket.versioning,
        });
    }, [bucket]);

    // Fetch credentials on mount
    useEffect(() => {
        const fetchCredentials = async () => {
            setCredentialsLoading(true);
            setCredentialsError(null);
            try {
                const response = await bucketService.getCredentials(bucket.id, projectId);
                setCredentials(response.data);
            } catch (err: any) {
                setCredentialsError(err?.response?.data?.error || 'Failed to load credentials');
            } finally {
                setCredentialsLoading(false);
            }
        };
        fetchCredentials();
    }, [bucket.id, projectId]);

    // Check if there are unsaved changes
    const hasChanges = useMemo(() => {
        const currentVisibility = bucket.is_public ? 'public' : 'private';
        return (
            formData.visibility !== currentVisibility ||
            formData.description !== (bucket.description || '') ||
            formData.versioning !== bucket.versioning
        );
    }, [formData, bucket]);

    // Copy to clipboard helper
    const handleCopy = async (text: string, field: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Copy bucket ID to clipboard
    const handleCopyId = async () => {
        try {
            await navigator.clipboard.writeText(bucket.id);
            setCopiedId(true);
            setTimeout(() => setCopiedId(false), 2000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    // Handle save
    const handleSave = async () => {
        const updates: UpdateBucketData = {};

        // Only include changed fields
        const currentVisibility = bucket.is_public ? 'public' : 'private';
        if (formData.visibility !== currentVisibility) {
            updates.is_public = formData.visibility === 'public';
        }
        if (formData.description !== (bucket.description || '')) {
            updates.description = formData.description;
        }
        if (formData.versioning !== bucket.versioning) {
            updates.versioning = formData.versioning;
        }

        if (Object.keys(updates).length > 0) {
            await onUpdate(updates);
        }
    };

    return (
        <div className="space-y-6">
            {/* Overview Section */}
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Overview</h3>

                <div className="space-y-4">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                            Name
                        </label>
                        <p className="text-white font-medium">{bucket.name}</p>
                    </div>

                    {/* Visibility Badge */}
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                            Visibility
                        </label>
                        <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bucket.is_public
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-gray-700 text-gray-300'
                                }`}
                        >
                            {bucket.is_public ? 'Public' : 'Private'}
                        </span>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                            Description
                        </label>
                        <p className="text-white">
                            {bucket.description || <span className="text-gray-400">-</span>}
                        </p>
                    </div>

                    {/* Versioning State */}
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                            Versioning
                        </label>
                        <span
                            className={`text-sm font-medium ${bucket.versioning ? 'text-green-400' : 'text-gray-400'
                                }`}
                        >
                            {bucket.versioning ? 'On' : 'Off'}
                        </span>
                    </div>

                    {/* Bucket ID - Copyable */}
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                            Bucket ID
                        </label>
                        <div className="flex items-center gap-2">
                            <code className="text-sm bg-gray-700 px-2 py-1 rounded text-gray-300 font-mono">
                                {bucket.id}
                            </code>
                            <button
                                onClick={handleCopyId}
                                className="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded transition-colors"
                                title="Copy to clipboard"
                            >
                                {copiedId ? (
                                    <CheckIcon className="w-4 h-4 text-green-400" />
                                ) : (
                                    <ClipboardDocumentIcon className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Metadata */}
                    <div className="pt-4 border-t border-gray-700">
                        <h4 className="text-sm font-medium text-gray-400 mb-3">Metadata</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">
                                    Created at
                                </label>
                                <p
                                    className="text-sm text-gray-300"
                                    title={bucket.created_at}
                                >
                                    {format(new Date(bucket.created_at), 'MMM d, yyyy h:mm a')}
                                </p>
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">
                                    Created by
                                </label>
                                <p className="text-sm text-gray-300">
                                    {bucket.created_by || <span className="text-gray-400">-</span>}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Credentials Section */}
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                    <KeyIcon className="w-5 h-5 text-primary-400" />
                    <h3 className="text-lg font-semibold text-white">Credentials</h3>
                </div>

                {credentialsLoading ? (
                    <div className="space-y-3">
                        <div className="h-4 bg-gray-700 rounded animate-pulse w-1/4" />
                        <div className="h-8 bg-gray-700 rounded animate-pulse" />
                        <div className="h-4 bg-gray-700 rounded animate-pulse w-1/4" />
                        <div className="h-8 bg-gray-700 rounded animate-pulse" />
                        <div className="h-4 bg-gray-700 rounded animate-pulse w-1/4" />
                        <div className="h-8 bg-gray-700 rounded animate-pulse" />
                    </div>
                ) : credentialsError ? (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                        <InformationCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-400">{credentialsError}</p>
                    </div>
                ) : credentials ? (
                    <div className="space-y-4">
                        {/* Access Key */}
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                                Access Key
                            </label>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 text-sm bg-gray-800 border border-gray-700 px-3 py-2 rounded-lg text-gray-300 font-mono truncate">
                                    {credentials.access_key}
                                </code>
                                <button
                                    onClick={() => handleCopy(credentials.access_key, 'accessKey')}
                                    className="p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
                                    title="Copy access key"
                                >
                                    {copiedField === 'accessKey' ? (
                                        <CheckIcon className="w-4 h-4 text-green-400" />
                                    ) : (
                                        <ClipboardDocumentIcon className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Secret Key */}
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                                Secret Key
                            </label>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 text-sm bg-gray-800 border border-gray-700 px-3 py-2 rounded-lg text-gray-300 font-mono truncate">
                                    {showSecret ? credentials.secret_key : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                                </code>
                                <button
                                    onClick={() => setShowSecret(!showSecret)}
                                    className="p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
                                    title={showSecret ? 'Hide secret key' : 'Reveal secret key'}
                                >
                                    {showSecret ? (
                                        <EyeSlashIcon className="w-4 h-4" />
                                    ) : (
                                        <EyeIcon className="w-4 h-4" />
                                    )}
                                </button>
                                <button
                                    onClick={() => handleCopy(credentials.secret_key, 'secretKey')}
                                    className="p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
                                    title="Copy secret key"
                                >
                                    {copiedField === 'secretKey' ? (
                                        <CheckIcon className="w-4 h-4 text-green-400" />
                                    ) : (
                                        <ClipboardDocumentIcon className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Endpoint */}
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                                Endpoint
                            </label>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 text-sm bg-gray-800 border border-gray-700 px-3 py-2 rounded-lg text-gray-300 font-mono truncate">
                                    {credentials.minio_endpoint}
                                </code>
                                <button
                                    onClick={() => handleCopy(credentials.minio_endpoint, 'endpoint')}
                                    className="p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
                                    title="Copy endpoint"
                                >
                                    {copiedField === 'endpoint' ? (
                                        <CheckIcon className="w-4 h-4 text-green-400" />
                                    ) : (
                                        <ClipboardDocumentIcon className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Info note - clickable to show mount steps */}
                        <button
                            onClick={() => setShowMountSteps(!showMountSteps)}
                            className="w-full text-left p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg hover:bg-blue-500/15 transition-colors"
                        >
                            <div className="flex items-start gap-2">
                                <InformationCircleIcon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm text-blue-300">
                                        Compatible with s3fs-fuse. Access is scoped to this bucket only.
                                        <span className="ml-1 text-blue-400 underline underline-offset-2">
                                            {showMountSteps ? 'Hide mount steps' : 'Show mount steps'}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </button>

                        {/* s3fs-fuse mount steps */}
                        {showMountSteps && (
                            <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg space-y-3">
                                <h4 className="text-sm font-medium text-white">Mount with s3fs-fuse</h4>
                                <div className="space-y-2">
                                    <p className="text-xs text-gray-400">1. Install s3fs-fuse</p>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-xs bg-gray-900 border border-gray-700 px-3 py-2 rounded text-gray-300 font-mono">
                                            sudo apt-get install s3fs
                                        </code>
                                        <button
                                            onClick={() => handleCopy('sudo apt-get install s3fs', 'step1')}
                                            className="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded transition-colors"
                                        >
                                            {copiedField === 'step1' ? (
                                                <CheckIcon className="w-3.5 h-3.5 text-green-400" />
                                            ) : (
                                                <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                                            )}
                                        </button>
                                    </div>

                                    <p className="text-xs text-gray-400">2. Create credentials file</p>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-xs bg-gray-900 border border-gray-700 px-3 py-2 rounded text-gray-300 font-mono truncate">
                                            echo "{credentials.access_key}:{credentials.secret_key}" &gt; ~/.passwd-s3fs && chmod 600 ~/.passwd-s3fs
                                        </code>
                                        <button
                                            onClick={() => handleCopy(`echo "${credentials.access_key}:${credentials.secret_key}" > ~/.passwd-s3fs && chmod 600 ~/.passwd-s3fs`, 'step2')}
                                            className="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                                        >
                                            {copiedField === 'step2' ? (
                                                <CheckIcon className="w-3.5 h-3.5 text-green-400" />
                                            ) : (
                                                <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                                            )}
                                        </button>
                                    </div>

                                    <p className="text-xs text-gray-400">3. Mount the bucket</p>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-xs bg-gray-900 border border-gray-700 px-3 py-2 rounded text-gray-300 font-mono truncate">
                                            mkdir -p /mnt/{bucket.name} && s3fs {bucket.minio_bucket} /mnt/{bucket.name} -o url={credentials.minio_endpoint} -o use_path_request_style -o passwd_file=~/.passwd-s3fs
                                        </code>
                                        <button
                                            onClick={() => handleCopy(`mkdir -p /mnt/${bucket.name} && s3fs ${bucket.minio_bucket} /mnt/${bucket.name} -o url=${credentials.minio_endpoint} -o use_path_request_style -o passwd_file=~/.passwd-s3fs`, 'step3')}
                                            className="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                                        >
                                            {copiedField === 'step3' ? (
                                                <CheckIcon className="w-3.5 h-3.5 text-green-400" />
                                            ) : (
                                                <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : null}
            </div>

            {/* Settings Section */}
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Settings</h3>

                {/* Update Error Display */}
                {updateError && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                        <InformationCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-400">{updateError}</p>
                    </div>
                )}

                <div className="space-y-5">
                    {/* Visibility Toggle */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Visibility
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="private"
                                    checked={formData.visibility === 'private'}
                                    onChange={() =>
                                        setFormData({ ...formData, visibility: 'private' })
                                    }
                                    className="w-4 h-4 text-primary-500 border-gray-600 focus:ring-primary-500"
                                    disabled={isUpdating}
                                />
                                <span className="text-sm text-gray-300">Private</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="public"
                                    checked={formData.visibility === 'public'}
                                    onChange={() =>
                                        setFormData({ ...formData, visibility: 'public' })
                                    }
                                    className="w-4 h-4 text-primary-500 border-gray-600 focus:ring-primary-500"
                                    disabled={isUpdating}
                                />
                                <span className="text-sm text-gray-300">Public</span>
                            </label>
                        </div>
                        <p className="mt-1 text-xs text-gray-400">
                            Public buckets can be accessed without authentication
                        </p>
                    </div>

                    {/* Description Textarea */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) =>
                                setFormData({ ...formData, description: e.target.value })
                            }
                            className="input min-h-[80px] resize-none"
                            placeholder="Enter a description for this bucket..."
                            rows={3}
                            disabled={isUpdating}
                        />
                    </div>

                    {/* Versioning Toggle */}
                    <div>
                        <label className="flex items-center justify-between cursor-pointer">
                            <div>
                                <span className="text-sm font-medium text-gray-300">
                                    Versioning
                                </span>
                                <p className="text-xs text-gray-400">
                                    Keep multiple versions of objects
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() =>
                                    setFormData({ ...formData, versioning: !formData.versioning })
                                }
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${formData.versioning ? 'bg-primary-500' : 'bg-gray-600'
                                    }`}
                                disabled={isUpdating}
                                role="switch"
                                aria-checked={formData.versioning}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.versioning ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                />
                            </button>
                        </label>
                    </div>

                    {/* Save Button */}
                    <div className="pt-4 border-t border-gray-700">
                        <button
                            onClick={handleSave}
                            disabled={!hasChanges || isUpdating}
                            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isUpdating ? 'Saving...' : 'Save Changes'}
                        </button>
                        {!hasChanges && (
                            <p className="mt-2 text-xs text-gray-400">
                                No changes to save
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Danger Zone Section */}
            <div className="bg-gray-900 rounded-xl shadow-sm border border-red-500/30 p-6">
                <div className="flex items-center gap-2 mb-4">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
                    <h3 className="text-lg font-semibold text-red-400">Danger Zone</h3>
                </div>

                <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-medium text-white">Delete this bucket</h4>
                            <p className="text-sm text-gray-400 mt-1">
                                Once you delete a bucket, there is no going back. All objects will be permanently removed.
                            </p>
                        </div>
                        <button
                            onClick={onDelete}
                            className="ml-4 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors whitespace-nowrap"
                        >
                            Delete Bucket
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BucketSettingsPanel;
